import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { reportsApi } from '../api/reports'
import { analyzeApi } from '../api/analyze'
import { ApiError } from '../lib/api'
import { formatDateBR, formatDateTimeBR } from '../lib/format'
import { Spinner } from '../components/Spinner'

export default function ReportErrors() {
  const qc = useQueryClient()
  const navigate = useNavigate()

  const { data, isLoading } = useQuery({
    queryKey: ['report-errors'],
    queryFn: () => reportsApi.errors(),
  })
  const reports = data?.reports ?? []

  const retry = useMutation({
    mutationFn: (vars: { id: string; clientId: string; weekStart: string; weekEnd: string }) =>
      analyzeApi.run(vars.clientId, {
        weekStart: vars.weekStart,
        weekEnd: vars.weekEnd,
        overwrite: true,
      }),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['report-errors'] })
      qc.invalidateQueries({ queryKey: ['reports'] })
      navigate(`/reports/${result.reportId}`)
    },
    onError: (err) =>
      alert(err instanceof ApiError ? err.message : 'Não foi possível regerar o relatório'),
  })

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Erros de geração</h1>
          <p className="mt-1 text-sm text-slate-500">
            Relatórios cuja geração <strong>falhou</strong> (Windsor, Claude/Gemini, etc.) ou que
            ficaram <strong>travados em processamento há mais de 30 min</strong> (geração que morreu
            no meio). Use "Regerar" para tentar de novo (substitui o relatório).
          </p>
        </div>
        {!isLoading && (
          <span className="rounded-full bg-slate-100 px-3 py-1 text-sm font-medium text-slate-600">
            {reports.length} com problema
          </span>
        )}
      </div>

      {isLoading ? (
        <Spinner />
      ) : reports.length === 0 ? (
        <p className="rounded-lg border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500">
          Nenhum relatório com problema. 🎉
        </p>
      ) : (
        <div className="space-y-3">
          {reports.map((r) => {
            const pending = retry.isPending && retry.variables?.id === r.id
            const stuck = r.status === 'processing'
            return (
              <div
                key={r.id}
                className={`rounded-xl border p-4 ${
                  stuck ? 'border-amber-200 bg-amber-50/50' : 'border-red-200 bg-red-50/40'
                }`}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="font-semibold text-slate-900">{r.client.name}</h2>
                      <span
                        className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                          stuck ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                        }`}
                      >
                        {stuck ? 'Travado em processamento' : 'Falha'}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500">
                      Semana {formatDateBR(r.weekStart)} a {formatDateBR(r.weekEnd)} ·{' '}
                      {stuck ? 'iniciado em' : 'falhou em'} {formatDateTimeBR(r.generatedAt)}
                    </p>
                  </div>
                  <button
                    onClick={() =>
                      retry.mutate({
                        id: r.id,
                        clientId: r.client.id,
                        weekStart: r.weekStart,
                        weekEnd: r.weekEnd,
                      })
                    }
                    disabled={retry.isPending}
                    className="inline-flex items-center gap-2 rounded-md bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
                  >
                    {pending && <Spinner className="h-4 w-4 text-white" />}
                    Regerar
                  </button>
                </div>
                {r.errorMessage ? (
                  <pre className="mt-3 max-h-48 overflow-auto whitespace-pre-wrap rounded-md bg-white/80 px-3 py-2 text-xs text-red-700">
                    {r.errorMessage}
                  </pre>
                ) : stuck ? (
                  <p className="mt-3 text-xs text-amber-700">
                    A geração não concluiu nem registrou erro — provavelmente o processo morreu no
                    meio. Regerar deve resolver.
                  </p>
                ) : null}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
